import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ThumbsUp, Timer, Download, Save, Share2 } from 'lucide-react';
import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { vibrate, HapticPatterns } from '../utils/haptics';

interface Exercise {
  id: string;
  title: string;
}

const FREEWRITE_PRESETS = [5, 10, 20, 30, 60, 90, 120];

export default function Practice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'prompt' | 'freewrite'>('prompt');
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [currentExercise, setCurrentExercise] = useState<Exercise | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [writing, setWriting] = useState('');
  const [freeWriting, setFreeWriting] = useState<string>(() => localStorage.getItem('practice_freewrite_draft') || '');
  const [freewriteDuration, setFreewriteDuration] = useState<number | null>(null);
  const [freewriteSecondsLeft, setFreewriteSecondsLeft] = useState<number>(0);
  const [isFreewriteRunning, setIsFreewriteRunning] = useState(false);
  const [freewriteComplete, setFreewriteComplete] = useState(false);
  const [saveMode, setSaveMode] = useState<'save' | 'share' | null>(null);
  const [saved, setSaved] = useState(false);
  const [savedWritings, setSavedWritings] = useState<{[key: string]: string}>(() => {
    const saved = localStorage.getItem('practice_writings');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    if (user) {
      fetchExercises();
    }
  }, [user]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  useEffect(() => {
    localStorage.setItem('practice_writings', JSON.stringify(savedWritings));
  }, [savedWritings]);

  useEffect(() => {
    localStorage.setItem('practice_freewrite_draft', freeWriting);
  }, [freeWriting]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isFreewriteRunning && freewriteSecondsLeft > 0) {
      interval = setInterval(() => {
        setFreewriteSecondsLeft(prev => {
          if (prev <= 1) {
            setIsFreewriteRunning(false);
            setFreewriteComplete(true);
            vibrate(HapticPatterns.SUCCESS);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isFreewriteRunning, freewriteSecondsLeft]);

  const fetchExercises = async () => {
    try {
      console.log('Fetching exercises with user:', user?.uid);
      setError(null);
      const querySnapshot = await getDocs(collection(db, 'exercises'));
      console.log('Got querySnapshot:', querySnapshot.size, 'documents');
      const exerciseData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Exercise[];
      console.log('Parsed exercise data:', exerciseData.length, 'exercises');
      setExercises(exerciseData);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching exercises:', error);
      setError(error?.message || 'Failed to load exercises');
      setLoading(false);
    }
  };

  const generateExercise = () => {
    console.log('Generating exercise from', exercises.length, 'exercises');
    if (exercises.length === 0) {
      setError('No exercises available');
      return;
    }
    const randomIndex = Math.floor(Math.random() * exercises.length);
    const newExercise = exercises[randomIndex];
    console.log('Selected exercise:', newExercise);
    setCurrentExercise(newExercise);
    setTimer(0);
    setIsTimerRunning(false);
    setWriting(savedWritings[newExercise.id] || '');
    setSaveMode(null);
    setSaved(false);
  };

  const toggleTimer = () => {
    setIsTimerRunning(!isTimerRunning);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleModeChange = (nextMode: 'prompt' | 'freewrite') => {
    if (mode === 'prompt' && isTimerRunning) setIsTimerRunning(false);
    if (mode === 'freewrite' && isFreewriteRunning) setIsFreewriteRunning(false);
    setMode(nextMode);
  };

  const selectFreewriteDuration = (minutes: number) => {
    setFreewriteDuration(minutes);
    setFreewriteSecondsLeft(minutes * 60);
    setIsFreewriteRunning(false);
    setFreewriteComplete(false);
  };

  const resetFreewriteDuration = () => {
    setFreewriteDuration(null);
    setFreewriteSecondsLeft(0);
    setIsFreewriteRunning(false);
    setFreewriteComplete(false);
  };

  const toggleFreewriteTimer = () => {
    if (freewriteComplete) return;
    setIsFreewriteRunning(prev => !prev);
  };

  const activeWriting = mode === 'prompt' ? writing : freeWriting;
  const setActiveWriting = mode === 'prompt' ? setWriting : setFreeWriting;
  const activeWordCount = activeWriting.trim() ? activeWriting.trim().split(/\s+/).length : 0;
  const canShowWritingArea = mode === 'prompt' ? !!currentExercise : freewriteDuration !== null;
  const sessionHeader = mode === 'prompt'
    ? `Exercise: ${currentExercise?.title}`
    : `Free Writing (${freewriteDuration} min)`;

  const handleDownload = () => {
    if (!canShowWritingArea || !activeWriting) return;

    const element = document.createElement('a');
    const file = new Blob([
      `${sessionHeader}\n\n${activeWriting}`,
    ], { type: 'text/plain' });

    element.href = URL.createObjectURL(file);
    element.download = `writing-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSave = async (shareAfter: boolean) => {
    if (!user || !canShowWritingArea || !activeWriting.trim()) return;
    setSaveMode(shareAfter ? 'share' : 'save');
    setError(null);
    try {
      if (mode === 'prompt' && currentExercise) {
        setSavedWritings(prev => ({ ...prev, [currentExercise.id]: writing }));
      }

      if (shareAfter) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.exists() ? userDoc.data() : null;
          const username = userData?.username || 'Anonymous';

          await addDoc(collection(db, 'posts'), {
            content: `${sessionHeader}\n\n${activeWriting}`,
            authorId: user.uid,
            authorName: username,
            authorPhoto: user.photoURL || '',
            createdAt: new Date(),
            likes: 0,
            comments: 0,
            shares: 0,
            likedBy: [],
            tags: mode === 'prompt' ? ['practice'] : ['practice', 'freewrite']
          });
        } catch (shareErr) {
          console.error('Error sharing practice:', shareErr);
          setError('Saved, but failed to share to feed.');
        }
      }

      setSaved(true);

      if (shareAfter) {
        setTimeout(() => {
          navigate('/community');
          window.scrollTo(0, 0);
        }, 1200);
      }
    } catch (error) {
      console.error('Error saving practice:', error);
      setError('Failed to save practice');
      setSaveMode(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-0 md:p-4 text-center text-gray-400">
        Loading exercises...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-0 md:p-4">
        <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg text-center">
          {error}
          <button
            onClick={fetchExercises}
            className="ml-4 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-4 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-orange-500 mb-2">Practice Writing</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {mode === 'prompt'
            ? 'Get a random writing prompt to practice your comedy writing. Use these exercises to develop new material and sharpen your skills.'
            : "Set a timer and write freely with no prompt — great for warming up or exploring whatever's on your mind."}
        </p>
      </div>

      <div className="flex w-full lg:w-96 mx-auto bg-orange-50 border border-orange-100 rounded-xl p-1">
        <button
          onClick={() => handleModeChange('prompt')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            mode === 'prompt' ? 'bg-orange-500 text-white shadow' : 'text-orange-700 hover:bg-orange-100'
          }`}
        >
          Writing Prompt
        </button>
        <button
          onClick={() => handleModeChange('freewrite')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            mode === 'freewrite' ? 'bg-orange-500 text-white shadow' : 'text-orange-700 hover:bg-orange-100'
          }`}
        >
          Free Writing
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="space-y-6">
          {mode === 'prompt' ? (
            <>
              <div className="text-center">
                <p className={`mb-6 leading-snug ${currentExercise ? 'text-gray-900 text-xl sm:text-2xl font-bold' : 'text-gray-500 text-lg'}`}>
                  {currentExercise?.title || 'Click generate to get a writing prompt'}
                </p>

                <button
                  onClick={generateExercise}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2 mx-auto"
                >
                  <Play size={20} />
                  <span>Generate Exercise</span>
                </button>
              </div>

              {currentExercise && (
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                  <button
                    onClick={toggleTimer}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                      isTimerRunning
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    } transition-colors`}
                  >
                    <Timer size={20} />
                    <span>{isTimerRunning ? 'Stop' : 'Start'} Timer</span>
                  </button>
                  <div className="text-3xl font-mono font-black tabular-nums text-orange-500">
                    {formatTime(timer)}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {freewriteDuration === null ? (
                <div className="text-center">
                  <p className="text-gray-800 text-lg mb-4">Choose your session length</p>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {FREEWRITE_PRESETS.map((mins) => (
                      <button
                        key={mins}
                        onClick={() => selectFreewriteDuration(mins)}
                        className="rounded-2xl border-2 border-orange-100 bg-white hover:border-orange-400 hover:bg-orange-50 py-3 transition-all active:scale-95"
                      >
                        <span className="block text-xl font-extrabold text-orange-600 leading-none">{mins}</span>
                        <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">min</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center space-y-4">
                    <div className={`text-5xl sm:text-6xl font-mono font-black tabular-nums ${
                      freewriteSecondsLeft <= 60 && isFreewriteRunning ? 'text-red-500' : 'text-orange-500'
                    }`}>
                      {formatTime(freewriteSecondsLeft)}
                    </div>
                    <div className="w-full sm:w-96 mx-auto h-1.5 bg-orange-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full transition-all duration-1000 ease-linear"
                        style={{ width: `${freewriteDuration ? ((freewriteDuration * 60 - freewriteSecondsLeft) / (freewriteDuration * 60)) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                      <button
                        onClick={toggleFreewriteTimer}
                        disabled={freewriteComplete}
                        className={`px-5 py-2.5 rounded-full flex items-center space-x-2 font-bold disabled:opacity-50 ${
                          isFreewriteRunning
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md'
                        } transition-all active:scale-95`}
                      >
                        <Timer size={20} />
                        <span>{isFreewriteRunning ? 'Pause' : freewriteComplete ? 'Done' : 'Start'}</span>
                      </button>
                      <button
                        onClick={resetFreewriteDuration}
                        className="text-sm text-gray-500 underline hover:no-underline px-2 py-2"
                      >
                        Change duration
                      </button>
                    </div>
                  </div>
                  {freewriteComplete && (
                    <div className="bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 text-center">
                      <p className="text-orange-600 font-bold">
                        Time's up! Great work — save or keep writing.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {canShowWritingArea && (
            <div className="space-y-4">
              <textarea
                value={activeWriting}
                onChange={(e) => {
                  setActiveWriting(e.target.value);
                  setSaved(false);
                  setSaveMode(null);
                }}
                placeholder="Write your material here..."
                className="w-full min-h-[280px] bg-gray-50 border border-gray-200 rounded-2xl p-5 text-gray-800 placeholder-gray-400 text-[16px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm resize-none"
              />

              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="mr-auto text-xs text-gray-400 font-medium">
                  {activeWordCount} {activeWordCount === 1 ? 'word' : 'words'}
                </span>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold px-5 py-3 rounded-full transition-all active:scale-95"
                >
                  <Download size={18} />
                  <span>Download</span>
                </button>
                {activeWriting.trim() && (
                  <>
                    <button
                      onClick={() => handleSave(false)}
                      disabled={!!saveMode}
                      className="flex items-center gap-2 bg-orange-100 dark:bg-orange-500/10 hover:bg-orange-200 dark:hover:bg-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed text-orange-700 dark:text-orange-400 font-bold px-5 py-3 rounded-full transition-all active:scale-95"
                    >
                      <Save size={18} />
                      {saved && saveMode === 'save' ? 'Saved!' : saveMode === 'save' ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => handleSave(true)}
                      disabled={!!saveMode}
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded-full transition-all shadow-md active:scale-95"
                    >
                      <Share2 size={18} />
                      {saved && saveMode === 'share' ? 'Saved & Shared! Redirecting…' : saveMode === 'share' ? 'Sharing…' : 'Save & Share to Feed'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center">
            <div className="text-gray-500 text-sm flex items-center space-x-1">
              <ThumbsUp size={14} />
              <span>
                {mode === 'prompt'
                  ? 'Write for at least 10 minutes'
                  : freewriteDuration
                    ? `Stay focused for the full ${freewriteDuration} minutes`
                    : 'Pick a duration and get writing'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}